/**
 * Company Form Component
 *
 * Form for creating or editing a company
 */

import { useState, useEffect, useMemo, useRef, type ChangeEvent, type FormEvent } from 'react';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import FeatureGateTooltip from '@/hub-shared/components/FeatureGateTooltip';
import { getFecDsnImportGateFromBootstrap } from '@/hub-shared/gates/fecDsnImportGate';
import type { Company, CompanyFormData } from '../types/company';
import { __ } from '@/hub-shared/utils/i18n';
import { createCompany, updateCompany, handleApiError } from '../utils/api';
import CompanyDossierAccessPicker from '../shared/CompanyDossierAccessPicker';
import FecDsnUploadResponsibilityNotice from '../shared/FecDsnUploadResponsibilityNotice';
import MediaUploader from '../shared/MediaUploader';
import ProfileArtifactFileDropzone from '../shared/ProfileArtifactFileDropzone';
import CabinetFileImportWizard from './CabinetFileImportWizard';
import CompanyRegistryDropdown from './CompanyRegistryDropdown';
import type { RegistryCompanySuggestion } from '../types/company';
import {
  buildRegistrySearchQuery,
  useCompanyRegistrySearch,
  type RegistrySearchField,
} from '../hooks/useCompanyRegistrySearch';
import { COMPANY_PROFILE_ARTIFACT_FILE_ACCEPT } from '../constants/companyProfileImport';
import type { PortalSearchSyncFn } from '@/portal/src/utils/portalUrlState';
import { parsePortalSearchParams } from '@/portal/src/utils/portalUrlState';
import { getBootstrapData } from '@tpct/admin-ui';

type CompanyFormProps = {
  company?: Company | null;
  onSave: (updatedCompany?: Company | null) => void;
  onCancel: () => void;
  bootstrapKey?: string;
  navigateTo?: (path: string) => void;
  /** When false, hide connector import (no active connections). Omitted = show if navigateTo (legacy / tests). */
  connectorImportVisible?: boolean;
  onProfileImportFileSelected?: (file: File) => void;
  /** Portal: after a cabinet file import job finishes, refresh lists (e.g. invalidate React Query). */
  onCabinetFileImportComplete?: () => void;
  /** Portal SPA: current location search — used with patchPortalSearch for cabinetImport deep link sync. */
  search?: string;
  patchPortalSearch?: PortalSearchSyncFn;
};

const CompanyForm = ({
  company,
  onSave,
  onCancel,
  bootstrapKey = 'AccountingHubCompaniesAdmin',
  navigateTo,
  connectorImportVisible,
  onProfileImportFileSelected,
  onCabinetFileImportComplete,
  search,
  patchPortalSearch,
}: CompanyFormProps) => {
  const isEdit = !!company;
  const [cabinetWizardOpen, setCabinetWizardOpen] = useState(false);
  const [cabinetWizardFiles, setCabinetWizardFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState<CompanyFormData>({
    name: company?.name || '',
    siren: company?.siren || '',
    fiscal_year_start: company?.fiscal_year_start || new Date().getFullYear() + '-01-01',
    fiscal_year_end: company?.fiscal_year_end || new Date().getFullYear() + '-12-31',
    thumbnail_id: company?.thumbnail_id,
    presentation_image_id: company?.presentation_image_id,
    headquarters_address: company?.headquarters_address || '',
    headquarters_postal_code: company?.headquarters_postal_code || '',
    headquarters_town: company?.headquarters_town || '',
    headquarters_country: company?.headquarters_country || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registryLastField, setRegistryLastField] = useState<RegistrySearchField>('name');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const identityRowRef = useRef<HTMLDivElement | null>(null);

  const registrySearch = useCompanyRegistrySearch(
    bootstrapKey,
    !isEdit,
    formData.name,
    formData.siren,
    registryLastField,
  );
  const closeRegistrySearch = registrySearch.close;

  const dossierAttributionManual = useMemo(() => {
    try {
      const bs = getBootstrapData(bootstrapKey);
      return bs.dossierAttributionDefaultScopeMode === 'explicit';
    } catch {
      return false;
    }
  }, [bootstrapKey]);

  const fecDsnImportGate = useMemo(() => {
    try {
      return getFecDsnImportGateFromBootstrap(getBootstrapData(bootstrapKey));
    } catch {
      return getFecDsnImportGateFromBootstrap({ isFecDsnImportEnabled: false });
    }
  }, [bootstrapKey]);
  const fecDsnImportDisabled = !fecDsnImportGate.allowed;

  // Every inbox and file-import route requires `perm.companies.import` (#1425).
  const canImportFiles = useMemo(() => {
    try {
      return getBootstrapData(bootstrapKey).capabilities?.importEnterprises === true;
    } catch {
      return false;
    }
  }, [bootstrapKey]);

  const [manualAttributionUserIds, setManualAttributionUserIds] = useState<string[]>([]);

  // Update form data when company changes (for edit mode)
  useEffect(() => {
    if (company) {
      setManualAttributionUserIds((company.manual_attribution_user_ids ?? []).map(String));
      setFormData({
        name: company.name || '',
        siren: company.siren || '',
        fiscal_year_start: company.fiscal_year_start || new Date().getFullYear() + '-01-01',
        fiscal_year_end: company.fiscal_year_end || new Date().getFullYear() + '-12-31',
        thumbnail_id: company.thumbnail_id,
        presentation_image_id: company.presentation_image_id,
        headquarters_address: company.headquarters_address || '',
        headquarters_postal_code: company.headquarters_postal_code || '',
        headquarters_town: company.headquarters_town || '',
        headquarters_country: company.headquarters_country || '',
      });
    }
  }, [company]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setFormSubmitted(true);
      form.reportValidity();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let updatedCompany: Company | null = null;
      const payload: CompanyFormData = { ...formData };
      if (dossierAttributionManual) {
        const ids = manualAttributionUserIds
          .map((id) => parseInt(id, 10))
          .filter((n) => Number.isFinite(n) && n > 0);
        payload.manual_attribution_user_ids = ids;
      }

      if (isEdit && company) {
        updatedCompany = await updateCompany(company.id, payload, bootstrapKey);
      } else {
        updatedCompany = await createCompany(payload, bootstrapKey);
      }
      // Pass updated company data back so parent can update selectedCompany
      onSave(updatedCompany);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      return undefined;
    }
    const onDocClick = (event: MouseEvent) => {
      if (identityRowRef.current && !identityRowRef.current.contains(event.target as Node)) {
        closeRegistrySearch();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [closeRegistrySearch, isEdit]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleIdentityChange = (field: RegistrySearchField, value: string) => {
    setRegistryLastField(field);
    registrySearch.setOpen(true);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRegistrySelect = (suggestion: RegistryCompanySuggestion) => {
    setFormData((prev) => ({
      ...prev,
      name: suggestion.name,
      siren: suggestion.siren,
      headquarters_address: suggestion.headquarters_address ?? prev.headquarters_address ?? '',
      headquarters_postal_code:
        suggestion.headquarters_postal_code ?? prev.headquarters_postal_code ?? '',
      headquarters_town: suggestion.headquarters_town ?? prev.headquarters_town ?? '',
      headquarters_country:
        suggestion.headquarters_country || prev.headquarters_country || __('France'),
    }));
    registrySearch.close();
    setError(null);
  };

  const handleProfileImportFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    e.target.value = '';
    if (fecDsnImportDisabled) {
      return;
    }
    if (!list || list.length === 0) {
      return;
    }
    if (navigateTo) {
      setCabinetWizardFiles(Array.from(list));
      if (patchPortalSearch) {
        patchPortalSearch({ cabinetImport: '1', cabinetImportStep: null }, 'push');
      } else {
        setCabinetWizardOpen(true);
      }
      return;
    }
    const file = list[0];
    if (file) {
      onProfileImportFileSelected?.(file);
    }
  };

  const showConnectorImportActive =
    !!navigateTo && (connectorImportVisible === undefined || connectorImportVisible);

  const portalCabinetImportOpen =
    !!patchPortalSearch && parsePortalSearchParams(search ?? '').get('cabinetImport') === '1';

  const cabinetWizardDisplayed =
    !!navigateTo &&
    (patchPortalSearch
      ? portalCabinetImportOpen && cabinetWizardFiles.length > 0
      : cabinetWizardOpen);

  const hideCreateChromeForWizard = !!navigateTo && cabinetWizardDisplayed;

  const connectorLinkIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const showCreateChrome = isEdit || !navigateTo || !hideCreateChromeForWizard;

  return (
    <div className="company-form container-style">
      
      {error && (
        <div className="notice notice-error">
          <p>{error}</p>
        </div>
      )}
      {!isEdit && showCreateChrome && (!navigateTo || showConnectorImportActive) ? (
        <div className="form-section company-form__bulk-connector">
          <h4 className="company-form__section-title">{__('Import depuis les connexions')}</h4>
          {!navigateTo ? (
            <button
              type="button"
              className={`dropzone ${loading ? 'disabled' : ''}`}
              disabled
              title={__('Disponible dans le portail')}
            >
              <div className="dropzone-content">
                <div className="dropzone-icon" aria-hidden="true">
                  {connectorLinkIcon}
                </div>
                <div className="dropzone-text">
                  <span className="dropzone-primary">{__('Importer depuis un connecteur')}</span>
                </div>
              </div>
            </button>
          ) : showConnectorImportActive ? (
            <button
              type="button"
              className={`dropzone ${loading ? 'disabled' : ''}`}
              disabled={loading}
              onClick={() => navigateTo('/companies/import-from-provider')}
            >
              <div className="dropzone-content">
                <div className="dropzone-icon" aria-hidden="true">
                  {connectorLinkIcon}
                </div>
                <div className="dropzone-text">
                  <span className="dropzone-primary">{__('Importer depuis un connecteur')}</span>
                </div>
              </div>
            </button>
          ) : null}
        </div>
      ) : null}
      {showCreateChrome ? (
        <form
          onSubmit={handleSubmit}
          className={`form-container form-container--wide${formSubmitted ? ' form--submitted' : ''}`}
          noValidate
        >
        {!isEdit ? (
          <h4 className="company-form__section-title">{__('Création manuelle')}</h4>
        ) : null}

        <div className="form-section">
          <div className="company-form__identity-row" ref={identityRowRef}>
            <div className="form-row">
              <div className="form-field company-form__registry-field">
                <label htmlFor="name">
                  {__('Raison sociale')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => handleIdentityChange('name', e.target.value)}
                  onFocus={() => {
                    setRegistryLastField('name');
                    registrySearch.setOpen(true);
                  }}
                  required
                  disabled={loading}
                  autoComplete="off"
                />
                {!isEdit && registryLastField === 'name' ? (
                  <CompanyRegistryDropdown
                    open={registrySearch.open}
                    results={registrySearch.results}
                    loading={registrySearch.loading}
                    error={registrySearch.error}
                    hasSearched={registrySearch.hasSearched}
                    queryLength={buildRegistrySearchQuery(
                      formData.name,
                      formData.siren,
                      'name',
                    ).length}
                    onPick={handleRegistrySelect}
                  />
                ) : null}
              </div>

              <div className="form-field company-form__registry-field">
                <label htmlFor="siren">
                  SIREN <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="siren"
                  name="siren"
                  value={formData.siren}
                  onChange={(e) => handleIdentityChange('siren', e.target.value)}
                  onFocus={() => {
                    setRegistryLastField('siren');
                    registrySearch.setOpen(true);
                  }}
                  required
                  pattern="[0-9]{9}"
                  maxLength={9}
                  disabled={loading}
                  placeholder="123456789"
                  autoComplete="off"
                />
                {!isEdit && registryLastField === 'siren' ? (
                  <CompanyRegistryDropdown
                    open={registrySearch.open}
                    results={registrySearch.results}
                    loading={registrySearch.loading}
                    error={registrySearch.error}
                    hasSearched={registrySearch.hasSearched}
                    queryLength={buildRegistrySearchQuery(
                      formData.name,
                      formData.siren,
                      'siren',
                    ).length}
                    onPick={handleRegistrySelect}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <CompanyDossierAccessPicker
          bootstrapKey={bootstrapKey}
          idPrefix={isEdit ? 'company-edit-manual-attribution' : 'company-create-manual-attribution'}
          value={manualAttributionUserIds}
          onChange={setManualAttributionUserIds}
          disabled={loading}
          description={
            isEdit
              ? __('Choisissez les membres du cabinet qui auront accès à ce dossier.')
              : __('Choisissez les membres du cabinet qui auront accès à ce dossier après création.')
          }
        />

        <div className="form-section">
          <div className="form-field">
            <label htmlFor="headquarters_address">
              {__('Adresse')}
            </label>
            <input
              type="text"
              id="headquarters_address"
              name="headquarters_address"
              value={formData.headquarters_address || ''}
              onChange={handleChange}
              disabled={loading}
              placeholder={__('Numéro et nom de rue')}
            />
          </div>

          <div className="form-row" style={{ gridTemplateColumns: '1fr 2fr 1fr' }}>
            <div className="form-field">
              <label htmlFor="headquarters_postal_code">
                {__('Code postal')}
              </label>
              <input
                type="text"
                id="headquarters_postal_code"
                name="headquarters_postal_code"
                value={formData.headquarters_postal_code || ''}
                onChange={handleChange}
                disabled={loading}
                placeholder="75001"
                maxLength={10}
              />
            </div>

            <div className="form-field">
              <label htmlFor="headquarters_town">
                {__('Ville')}
              </label>
              <input
                type="text"
                id="headquarters_town"
                name="headquarters_town"
                value={formData.headquarters_town || ''}
                onChange={handleChange}
                disabled={loading}
                placeholder={__('Paris')}
              />
            </div>

            <div className="form-field">
              <label htmlFor="headquarters_country">
                {__('Pays')}
              </label>
              <input
                type="text"
                id="headquarters_country"
                name="headquarters_country"
                value={formData.headquarters_country || ''}
                onChange={handleChange}
                disabled={loading}
                placeholder={__('France')}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4 className="company-form__section-title">{__('Exercice fiscal')}</h4>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="fiscal_year_start">
                {__("Début d'exercice")} <span className="required">*</span>
              </label>
              <input
                type="date"
                id="fiscal_year_start"
                name="fiscal_year_start"
                value={formData.fiscal_year_start}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-field">
              <label htmlFor="fiscal_year_end">
                {__("Fin d'exercice")} <span className="required">*</span>
              </label>
              <input
                type="date"
                id="fiscal_year_end"
                name="fiscal_year_end"
                value={formData.fiscal_year_end}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-row">
            <MediaUploader
              value={formData.thumbnail_id}
              media={company?.thumbnail_media}
              onChange={(thumbnailId: number | undefined) => {
                setFormData(prev => ({
                  ...prev,
                  thumbnail_id: thumbnailId,
                }));
              }}
              label={__("Logo de l'entreprise")}
              description={__('Affiché dans les rapports générés.')}
              disabled={loading}
              bootstrapKey={bootstrapKey}
            />

            <MediaUploader
              value={formData.presentation_image_id}
              media={company?.presentation_media}
              onChange={(id: number | undefined) => {
                setFormData(prev => ({
                  ...prev,
                  presentation_image_id: id,
                }));
              }}
              label={__('Image de présentation')}
              description={__('Image haute qualité dans le corps du rapport.')}
              disabled={loading}
              bootstrapKey={bootstrapKey}
            />
          </div>
        </div>

        {!isEdit && canImportFiles ? (
          <div className="form-section">
            <div className="form-field">
              <label htmlFor="company-profile-import-file">{__('FEC / DSN')}</label>
              <FecDsnUploadResponsibilityNotice />
              <FeatureGateNotice decision={fecDsnImportGate} />
              <FeatureGateTooltip decision={fecDsnImportGate}>
                <ProfileArtifactFileDropzone
                  inputId="company-profile-import-file"
                  accept={COMPANY_PROFILE_ARTIFACT_FILE_ACCEPT}
                  multiple={!!navigateTo}
                  disabled={loading || fecDsnImportDisabled}
                ariaLabel={
                  navigateTo
                    ? __('Choisir un ou plusieurs fichiers FEC, CSV, TXT ou DSN')
                    : __('Choisir un fichier FEC, CSV, TXT ou DSN')
                }
                primaryLabel={
                  navigateTo ? __('Importer un ou plusieurs fichiers') : __('Importer un fichier')
                }
                onChange={handleProfileImportFileChange}
                />
              </FeatureGateTooltip>
            </div>
          </div>
        ) : null}

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {__('Annuler')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? __('Enregistrement...') : isEdit ? __('Enregistrer') : __('Créer')}
          </button>
        </div>
          </form>
      ) : null}
      {navigateTo ? (
        <CabinetFileImportWizard
          variant="inline"
          open={cabinetWizardDisplayed}
          files={cabinetWizardFiles}
          bootstrapKey={bootstrapKey}
          onClose={() => {
            setCabinetWizardFiles([]);
            if (patchPortalSearch) {
              patchPortalSearch({ cabinetImport: null, cabinetImportStep: null }, 'replace');
            } else {
              setCabinetWizardOpen(false);
            }
          }}
          onImported={onCabinetFileImportComplete}
          search={search}
          patchPortalSearch={patchPortalSearch}
        />
      ) : null}
    </div>
  );
};

export default CompanyForm;
